import React, { useState, useEffect } from 'react';
import { ModelReferenceUpdate, typesService } from '../../services/typesService';
import { clientService } from '../../services/clientService';
import { Client } from '../../types/client';
import { useI18n } from '../../contexts/I18nContext';

interface ModelRefWithHardware {
  id: number;
  code: string;
  name?: string;
  description?: string;
  is_active: boolean;
  client_ids?: string[];
  hardware_type_id: number;
  hardware_type_code: string;
  hardware_type_name: string;
  created_at: string;
  updated_at: string;
}

interface EditModelReferenceModalProps {
  isOpen: boolean;
  modelRef: ModelRefWithHardware | null;
  onClose: () => void;
  onUpdated: () => void;
  onUpdate: (id: number, data: ModelReferenceUpdate) => Promise<void>;
}

interface FormData {
  code: string;
  name: string;
  description: string;
  is_active: boolean;
}

interface FormErrors {
  [key: string]: string;
}

const GLOBAL_CLIENT = '00000000-0000-0000-0000-000000000000';

/**
 * One model reference: its code, name, status, and the clients that may
 * use it. The client list is saved as it is edited; the rest on Save.
 */
export const EditModelReferenceModal: React.FC<EditModelReferenceModalProps> = ({
  isOpen,
  modelRef,
  onClose,
  onUpdated,
  onUpdate
}) => {
  const { t } = useI18n();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<FormErrors>({});
  const [formData, setFormData] = useState<FormData>({ code: '', name: '', description: '', is_active: true });

  // Client management state
  const [clientIds, setClientIds] = useState<string[]>([]);
  const [allClients, setAllClients] = useState<Client[]>([]);
  const [selectedClientToAdd, setSelectedClientToAdd] = useState<string>('');
  const [isLoadingClients, setIsLoadingClients] = useState(false);
  const [clientNames, setClientNames] = useState<Map<string, string>>(new Map());

  useEffect(() => {
    if (modelRef) {
      setFormData({
        code: modelRef.code || '',
        name: modelRef.name || '',
        description: modelRef.description || '',
        is_active: modelRef.is_active
      });
      setClientIds(modelRef.client_ids || [GLOBAL_CLIENT]);
      setErrors({});
    }
  }, [modelRef]);

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    clientService.fetchActiveClients()
      .then((clients) => {
        if (cancelled) return;
        setAllClients(clients);
        const namesMap = new Map<string, string>();
        clients.forEach(c => namesMap.set(c.id, c.name));
        setClientNames(namesMap);
      })
      .catch((err) => { console.error('Failed to load clients:', err); });
    return () => { cancelled = true; };
  }, [isOpen]);

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};
    if (!formData.code.trim()) newErrors.code = t('deviceTypes.validation.refCodeRequired');
    else if (!/^[A-Z0-9-]{2,20}$/.test(formData.code)) newErrors.code = t('deviceTypes.validation.refCodeFormat');
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field: keyof FormData, value: string | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: '' }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm() || !modelRef) return;
    setIsSubmitting(true);
    try {
      await onUpdate(modelRef.id, {
        code: formData.code.trim(),
        name: formData.name.trim() || undefined,
        description: formData.description.trim() || undefined,
        is_active: formData.is_active
      });
      onUpdated();
      onClose();
    } catch (error: any) {
      const errorMessage = error?.response?.data?.detail || (error instanceof Error ? error.message : t('deviceTypes.modelRef.updateFailed'));
      setErrors({ submit: errorMessage });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setErrors({});
    onClose();
  };

  const getClientName = (clientId: string): string => {
    if (clientId === GLOBAL_CLIENT) return t('deviceTypes.modelRef.global');
    return clientNames.get(clientId) || clientId;
  };

  const handleAddClient = async () => {
    if (!selectedClientToAdd || !modelRef) return;
    setIsLoadingClients(true);
    setErrors(prev => ({ ...prev, clients: '' }));
    try {
      const result = await typesService.addModelReferenceClient(modelRef.id, selectedClientToAdd);
      setClientIds(result.client_ids);
      setSelectedClientToAdd('');
    } catch (err: any) {
      setErrors(prev => ({ ...prev, clients: err.response?.data?.detail || err.message || t('deviceTypes.modelRef.clientsAddFailed') }));
    } finally {
      setIsLoadingClients(false);
    }
  };

  const handleRemoveClient = async (clientIdToRemove: string) => {
    if (!modelRef) return;
    if (clientIds.length <= 1) {
      setErrors(prev => ({ ...prev, clients: t('deviceTypes.modelRef.clientsLast') }));
      return;
    }
    setIsLoadingClients(true);
    setErrors(prev => ({ ...prev, clients: '' }));
    try {
      const result = await typesService.removeModelReferenceClient(modelRef.id, clientIdToRemove);
      setClientIds(result.client_ids);
    } catch (err: any) {
      setErrors(prev => ({ ...prev, clients: err.response?.data?.detail || err.message || t('deviceTypes.modelRef.clientsRemoveFailed') }));
    } finally {
      setIsLoadingClients(false);
    }
  };

  const availableClientsToAdd = allClients.filter(
    c => !clientIds.some(id => id.replace(/-/g, '').toLowerCase() === c.id.replace(/-/g, '').toLowerCase())
  );

  if (!isOpen || !modelRef) return null;

  return (
    <div className="modal-overlay" onClick={handleClose}>
      <div className="config-template-detail-modal tpd dtm" data-testid="modal-edit-model-reference" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-title tpd-title">
            <span className="tpd-mark" aria-hidden="true">{modelRef.hardware_type_code}</span>
            <div className="tpd-title-text">
              <h2>{t('deviceTypes.editModelReference')}</h2>
              <div className="tpd-title-sub">
                <span className="asg-template-name">{modelRef.code}</span>
                <span className="asg-sep">·</span>
                <span>{modelRef.hardware_type_name}</span>
                {!modelRef.is_active && <span className="tpd-off">{t('common.inactive')}</span>}
              </div>
            </div>
          </div>
          <button type="button" className="close-button" data-testid="btn-modal-close" onClick={handleClose} aria-label={t('common.close')}>&times;</button>
        </div>

        <form onSubmit={handleSubmit} noValidate className="tpc-form">
          <div className="modal-content tpd-body">
            {errors.submit && <div className="error-message" role="alert">{errors.submit}</div>}

            <section className="cu-section">
              <header className="cu-section-head">
                <h3>{t('deviceTypes.referenceDetails')}</h3>
                <p>{t('deviceTypes.modelRef.detailsIntro')}</p>
              </header>
              <div className="cu-grid">
                <div className="cu-field">
                  <label htmlFor="emrType">{t('deviceTypes.hardwareType')}</label>
                  <input type="text" id="emrType" value={`${modelRef.hardware_type_name} (${modelRef.hardware_type_code})`} className="cu-input" disabled />
                  <span className="cu-hint">{t('deviceTypes.hardwareTypeCannotChange')}</span>
                </div>
                <div className="cu-field">
                  <label htmlFor="emrStatus">{t('clients.status')}</label>
                  <select
                    id="emrStatus"
                    data-testid="field-modelref-is-active"
                    value={formData.is_active ? 'active' : 'inactive'}
                    onChange={(e) => handleInputChange('is_active', e.target.value === 'active')}
                    className="cu-input"
                    disabled={isSubmitting}
                  >
                    <option value="active">{t('common.active')}</option>
                    <option value="inactive">{t('common.inactive')}</option>
                  </select>
                  <span className="cu-hint">{t('deviceTypes.modelRef.statusIntro')}</span>
                </div>
                <div className="cu-field">
                  <label htmlFor="emrCode">{t('deviceTypes.referenceCode')} *</label>
                  <input
                    type="text"
                    id="emrCode"
                    data-testid="field-modelref-code-edit"
                    value={formData.code}
                    onChange={(e) => handleInputChange('code', e.target.value.toUpperCase())}
                    className={`cu-input mono${errors.code ? ' is-invalid' : ''}`}
                    placeholder={t('deviceTypes.modelRef.codePlaceholder')}
                    maxLength={20}
                    disabled={isSubmitting}
                  />
                  {errors.code ? <span className="cu-error" data-testid="error-modelref-code">{errors.code}</span> : <span className="cu-hint">{t('deviceTypes.modelRef.codeHint')}</span>}
                </div>
                <div className="cu-field">
                  <label htmlFor="emrName">{t('deviceTypes.name')}</label>
                  <input
                    type="text"
                    id="emrName"
                    data-testid="field-modelref-name-edit"
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    className="cu-input"
                    placeholder={t('deviceTypes.modelRef.namePlaceholder')}
                    disabled={isSubmitting}
                  />
                  <span className="cu-hint">{t('deviceTypes.modelRef.nameHint')}</span>
                </div>
                <div className="cu-field cu-field-wide">
                  <label htmlFor="emrDescription">{t('common.description')}</label>
                  <textarea
                    id="emrDescription"
                    data-testid="field-modelref-description-edit"
                    value={formData.description}
                    onChange={(e) => handleInputChange('description', e.target.value)}
                    className="cu-input"
                    placeholder={t('deviceTypes.modelRefDescriptionPlaceholder')}
                    rows={2}
                    disabled={isSubmitting}
                  />
                  <span className="cu-hint">{t('deviceTypes.modelRef.descriptionHint')}</span>
                </div>
              </div>
            </section>

            <section className="cu-section">
              <header className="cu-section-head">
                <h3>{t('deviceTypes.associatedClients')}<span className="ud-tab-count">{clientIds.length}</span></h3>
                <p>{t('deviceTypes.modelRef.clientsIntro')}</p>
              </header>

              {errors.clients && <div className="error-message" role="alert">{errors.clients}</div>}

              {clientIds.length > 0 ? (
                <ul className="ud-list dt-client-list" data-testid="modelref-clients">
                  {clientIds.map((clientId) => {
                    const isGlobal = clientId === GLOBAL_CLIENT;
                    return (
                      <li key={clientId} className="ud-row">
                        <div className="ud-row-text">
                          {isGlobal ? <span className="tpl-owner-anep">{t('deviceTypes.modelRef.global')}</span> : <span>{getClientName(clientId)}</span>}
                        </div>
                        {clientIds.length > 1 && (
                          <button type="button" className="tpl-link is-danger" onClick={() => handleRemoveClient(clientId)} disabled={isLoadingClients} data-testid={`btn-modelref-remove-client-${clientId}`}>
                            {t('deviceTypes.detail.remove')}
                          </button>
                        )}
                      </li>
                    );
                  })}
                </ul>
              ) : (
                <div className="tpd-empty"><p>{t('deviceTypes.modelRef.clientsNone')}</p></div>
              )}

              <div className="cu-input-row">
                <select
                  value={selectedClientToAdd}
                  onChange={(e) => setSelectedClientToAdd(e.target.value)}
                  className="cu-input"
                  aria-label={t('deviceTypes.modelRef.clientsAdd')}
                  data-testid="field-modelref-add-client"
                  disabled={isLoadingClients}
                >
                  <option value="">{t('deviceTypes.modelRef.clientsAdd')}</option>
                  {availableClientsToAdd.map(client => (
                    <option key={client.id} value={client.id}>{client.name}</option>
                  ))}
                </select>
                <button type="button" className="btn btn-secondary btn-sm" onClick={handleAddClient} disabled={isLoadingClients || !selectedClientToAdd} data-testid="btn-modelref-add-client">
                  {isLoadingClients ? t('deviceTypes.modelRef.clientsAdding') : t('deviceTypes.modelRef.clientsAddBtn')}
                </button>
              </div>
            </section>
          </div>

          <div className="modal-actions tpd-footer">
            <button type="button" data-testid="btn-modelref-cancel" onClick={handleClose} className="btn btn-secondary" disabled={isSubmitting}>
              {t('common.cancel')}
            </button>
            <button type="submit" data-testid="btn-modelref-save" className="btn btn-primary" disabled={isSubmitting}>
              {isSubmitting ? t('common.saving') : t('common.save')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
